import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Calendar, User, Building, ChevronLeft, ChevronRight, AlertCircle, FileText, Trash2, Check, Image, Archive } from 'lucide-react';
import axios from 'axios';
import AdminNavigation from '../components/AdminNavigation';

const SubmissionsPage = () => {
  const navigate = useNavigate();
  const [dashboardState, setDashboardState] = useState({
    loading: true,
    error: null,
    submissions: [],
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      pages: 0
    }
  });

  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null
  });

  const [selectionState, setSelectionState] = useState({
    selectedIds: new Set(),
    isDeleting: false,
    isArchiving: false,
    isAssigning: false,
    showDeleteConfirm: false,
    showArchiveConfirm: false,
    showAssignConfirm: false,
    archiveReason: '',
    assignToUserId: null,
    assignToUserName: ''
  });

  const [assignmentState, setAssignmentState] = useState({
    users: [],
    loadingUsers: false,
    filterStatus: 'all' // 'all', 'assigned', 'unassigned'
  });

  useEffect(() => {
    checkAuthAndLoadData();
  }, [dashboardState.pagination.page]);

  useEffect(() => {
    // Load assignable users when component mounts
    loadAssignableUsers();
  }, []);

  const checkAuthAndLoadData = async () => {
    try {
      // Check authentication status
      const authResponse = await axios.get('/api/auth/status');
      
      if (!authResponse.data.isAuthenticated) {
        navigate('/admin');
        return;
      }
      
      setAuthState({
        isAuthenticated: true,
        user: authResponse.data.user
      });
      
      // Load submissions data
      await loadSubmissions();
      
    } catch (error) {
      console.error('Auth/data loading error:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      } else {
        setDashboardState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to load data. Please refresh the page.'
        }));
      }
    }
  };

  const loadSubmissions = async () => {
    setDashboardState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await axios.get('/api/submissions', {
        params: {
          page: dashboardState.pagination.page,
          limit: dashboardState.pagination.limit
        }
      });
      
      setDashboardState(prev => ({
        ...prev,
        loading: false,
        submissions: response.data.submissions,
        pagination: response.data.pagination
      }));
      
    } catch (error) {
      console.error('Load submissions error:', error);
      setDashboardState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.error || 'Failed to load submissions'
      }));
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout');
      navigate('/admin');
    } catch (error) {
      console.error('Logout error:', error);
      // Force navigation even if logout request fails
      navigate('/admin');
    }
  };

  const handlePageChange = (newPage) => {
    setDashboardState(prev => ({
      ...prev,
      pagination: { ...prev.pagination, page: newPage }
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return 'Not provided';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Selection handlers
  const handleSelectSubmission = (submissionId, checked) => {
    setSelectionState(prev => {
      const newSelectedIds = new Set(prev.selectedIds);
      if (checked) {
        newSelectedIds.add(submissionId);
      } else {
        newSelectedIds.delete(submissionId);
      }
      return { ...prev, selectedIds: newSelectedIds };
    });
  };

  const handleSelectAll = (checked) => {
    setSelectionState(prev => {
      if (checked) {
        const allIds = new Set(dashboardState.submissions.map(s => s.id));
        return { ...prev, selectedIds: allIds };
      } else {
        return { ...prev, selectedIds: new Set() };
      }
    });
  };

  const handleDeleteConfirm = () => {
    setSelectionState(prev => ({ ...prev, showDeleteConfirm: true }));
  };

  const handleDeleteCancel = () => {
    setSelectionState(prev => ({ ...prev, showDeleteConfirm: false }));
  };

  const handleArchiveConfirm = () => {
    setSelectionState(prev => ({ ...prev, showArchiveConfirm: true }));
  };

  const handleArchiveCancel = () => {
    setSelectionState(prev => ({ 
      ...prev, 
      showArchiveConfirm: false,
      archiveReason: ''
    }));
  };

  const handleBulkArchive = async () => {
    if (selectionState.selectedIds.size === 0) return;

    setSelectionState(prev => ({ ...prev, isArchiving: true }));

    try {
      const idsArray = Array.from(selectionState.selectedIds);
      
      await axios.post('/api/submissions/bulk-archive', {
        ids: idsArray,
        reason: selectionState.archiveReason
      });

      // Clear selection and reload data
      setSelectionState({
        selectedIds: new Set(),
        isDeleting: false,
        isArchiving: false,
        showDeleteConfirm: false,
        showArchiveConfirm: false,
        archiveReason: ''
      });

      // Reload submissions
      await loadSubmissions();

    } catch (error) {
      console.error('Archive error:', error);
      setDashboardState(prev => ({
        ...prev,
        error: error.response?.data?.error || 'Failed to archive submission(s)'
      }));
      setSelectionState(prev => ({ ...prev, isArchiving: false, showArchiveConfirm: false }));
    }
  };

  const handleBulkDelete = async () => {
    if (selectionState.selectedIds.size === 0) return;

    setSelectionState(prev => ({ ...prev, isDeleting: true }));

    try {
      const idsArray = Array.from(selectionState.selectedIds);
      
      if (idsArray.length === 1) {
        // Single delete
        await axios.delete(`/api/submissions/${idsArray[0]}`);
      } else {
        // Bulk delete
        await axios.delete('/api/submissions/bulk', {
          data: { ids: idsArray }
        });
      }

      // Clear selection and reload data
      setSelectionState({
        selectedIds: new Set(),
        isDeleting: false,
        isArchiving: false,
        showDeleteConfirm: false,
        showArchiveConfirm: false,
        archiveReason: ''
      });

      // Reload submissions
      await loadSubmissions();

    } catch (error) {
      console.error('Delete error:', error);
      setDashboardState(prev => ({
        ...prev,
        error: error.response?.data?.error || 'Failed to delete submission(s)'
      }));
      setSelectionState(prev => ({ ...prev, isDeleting: false, showDeleteConfirm: false }));
    }
  };

  // Load assignable users
  const loadAssignableUsers = async () => {
    setAssignmentState(prev => ({ ...prev, loadingUsers: true }));
    
    try {
      const response = await axios.get('/api/users/assignable');
      setAssignmentState(prev => ({
        ...prev,
        users: response.data.users,
        loadingUsers: false
      }));
    } catch (error) {
      console.error('Load assignable users error:', error);
      setAssignmentState(prev => ({ ...prev, loadingUsers: false }));
    }
  };

  // Assignment handlers
  const handleAssignConfirm = () => {
    setSelectionState(prev => ({ ...prev, showAssignConfirm: true }));
  };

  const handleAssignCancel = () => {
    setSelectionState(prev => ({ 
      ...prev, 
      showAssignConfirm: false,
      assignToUserId: null,
      assignToUserName: ''
    }));
  };

  const handleBulkAssign = async () => {
    if (selectionState.selectedIds.size === 0) return;

    setSelectionState(prev => ({ ...prev, isAssigning: true }));

    try {
      const idsArray = Array.from(selectionState.selectedIds);
      
      await axios.put('/api/submissions/bulk-assign', {
        submissionIds: idsArray,
        userId: selectionState.assignToUserId
      });

      // Clear selection and reload data
      setSelectionState({
        selectedIds: new Set(),
        isDeleting: false,
        isArchiving: false,
        isAssigning: false,
        showDeleteConfirm: false,
        showArchiveConfirm: false,
        showAssignConfirm: false,
        archiveReason: '',
        assignToUserId: null,
        assignToUserName: ''
      });

      // Reload submissions
      await loadSubmissions();

    } catch (error) {
      console.error('Assign error:', error);
      setDashboardState(prev => ({
        ...prev,
        error: error.response?.data?.error || 'Failed to assign submission(s)'
      }));
      setSelectionState(prev => ({ ...prev, isAssigning: false, showAssignConfirm: false }));
    }
  };

  const formatAssignedTo = (assignedTo) => {
    if (!assignedTo) return 'Unassigned';
    if (assignedTo.firstName && assignedTo.lastName) {
      return `${assignedTo.firstName} ${assignedTo.lastName}`;
    }
    return assignedTo.username;
  };

  // Reset selection when page changes
  useEffect(() => {
    setSelectionState(prev => ({ ...prev, selectedIds: new Set() }));
  }, [dashboardState.pagination.page]);

  if (dashboardState.loading && dashboardState.submissions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <AdminNavigation user={authState.user} onLogout={handleLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Form Submissions</h2>
          <p className="mt-2 text-sm text-gray-600">
            Manage all CC Jensen form submissions. Select multiple submissions for bulk actions.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FileText className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Submissions</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardState.pagination.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">This Page</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardState.submissions.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <User className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Page {dashboardState.pagination.page}</p>
                <p className="text-2xl font-bold text-gray-900">of {dashboardState.pagination.pages}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {dashboardState.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{dashboardState.error}</p>
                <button
                  onClick={() => loadSubmissions()}
                  className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Action Toolbar */}
        {selectionState.selectedIds.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Check className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-sm font-medium text-blue-800">
                  {selectionState.selectedIds.size} submission{selectionState.selectedIds.size > 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setSelectionState(prev => ({ ...prev, selectedIds: new Set() }))}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Clear selection
                </button>
                <button
                  onClick={handleAssignConfirm}
                  disabled={selectionState.isAssigning}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <User className="w-4 h-4 mr-1" />
                  {selectionState.isAssigning ? 'Assigning...' : 'Assign Selected'}
                </button>
                <button
                  onClick={handleArchiveConfirm}
                  disabled={selectionState.isArchiving}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Archive className="w-4 h-4 mr-1" />
                  {selectionState.isArchiving ? 'Archiving...' : 'Archive Selected'}
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={selectionState.isDeleting}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {selectionState.isDeleting ? 'Deleting...' : 'Delete Selected'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Submissions Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  All Submissions
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Complete list of all form submissions with management options.
                </p>
              </div>
              {dashboardState.submissions.length > 0 && (
                <div className="text-sm text-gray-500">
                  <span className="hidden sm:inline">Click on any row to view details. </span>
                  Select submissions for bulk actions.
                </div>
              )}
            </div>
            
            {/* Assignment Filter */}
            <div className="flex items-center gap-4">
              <label htmlFor="assignment-filter" className="text-sm font-medium text-gray-700">
                Filter by assignment:
              </label>
              <select
                id="assignment-filter"
                value={assignmentState.filterStatus}
                onChange={(e) => setAssignmentState(prev => ({ ...prev, filterStatus: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="all">All submissions</option>
                <option value="assigned">Assigned only</option>
                <option value="unassigned">Unassigned only</option>
              </select>
              {assignmentState.filterStatus !== 'all' && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Filtered: {assignmentState.filterStatus}
                </span>
              )}
            </div>
          </div>
          
          {dashboardState.submissions.filter(submission => {
            if (assignmentState.filterStatus === 'assigned') {
              return submission.assigned_to !== null;
            } else if (assignmentState.filterStatus === 'unassigned') {
              return submission.assigned_to === null;
            }
            return true; // 'all' case
          }).length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {assignmentState.filterStatus === 'all' ? 'No submissions' : `No ${assignmentState.filterStatus} submissions`}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {assignmentState.filterStatus === 'all' 
                  ? 'No form submissions have been received yet.'
                  : `No ${assignmentState.filterStatus} submissions found.`}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={
                          dashboardState.submissions.length > 0 &&
                          selectionState.selectedIds.size === dashboardState.submissions.length
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact Person
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned To
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submitted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dashboardState.submissions.filter(submission => {
                    if (assignmentState.filterStatus === 'assigned') {
                      return submission.assigned_to !== null;
                    } else if (assignmentState.filterStatus === 'unassigned') {
                      return submission.assigned_to === null;
                    }
                    return true; // 'all' case
                  }).map((submission) => (
                    <tr 
                      key={submission.id} 
                      className={`cursor-pointer transition-colors duration-150 ${selectionState.selectedIds.has(submission.id) ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'}`}
                      onClick={(e) => {
                        // Don't navigate if clicking on checkbox or action buttons
                        if (e.target.type !== 'checkbox' && !e.target.closest('button')) {
                          navigate(`/admin/submissions/${submission.id}`);
                        }
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={selectionState.selectedIds.has(submission.id)}
                          onChange={(e) => handleSelectSubmission(submission.id, e.target.checked)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{submission.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Building className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">
                            {truncateText(submission.company, 30)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {truncateText(submission.contact_person, 20)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {truncateText(submission.email, 25)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {submission.assigned_to ? (
                            <div className="flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                              <User className="h-3 w-3 mr-1" />
                              {formatAssignedTo(submission.assigned_to)}
                            </div>
                          ) : (
                            <div className="flex items-center px-2 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                              <span className="h-3 w-3 mr-1 rounded-full bg-gray-400"></span>
                              Unassigned
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(submission.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => navigate(`/admin/submissions/${submission.id}`)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {dashboardState.pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(dashboardState.pagination.page - 1)}
                disabled={dashboardState.pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(dashboardState.pagination.page + 1)}
                disabled={dashboardState.pagination.page === dashboardState.pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {((dashboardState.pagination.page - 1) * dashboardState.pagination.limit) + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(dashboardState.pagination.page * dashboardState.pagination.limit, dashboardState.pagination.total)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{dashboardState.pagination.total}</span>{' '}
                  results
                </p>
              </div>
              
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(dashboardState.pagination.page - 1)}
                    disabled={dashboardState.pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {dashboardState.pagination.page} of {dashboardState.pagination.pages}
                  </span>
                  
                  <button
                    onClick={() => handlePageChange(dashboardState.pagination.page + 1)}
                    disabled={dashboardState.pagination.page === dashboardState.pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {selectionState.showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                Delete Submissions
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete {selectionState.selectedIds.size} submission
                  {selectionState.selectedIds.size > 1 ? 's' : ''}? This action cannot be undone.
                </p>
                {selectionState.selectedIds.size > 1 && (
                  <p className="text-sm text-red-600 mt-2 font-medium">
                    This will permanently delete multiple submissions and their associated files.
                  </p>
                )}
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={handleDeleteCancel}
                    disabled={selectionState.isDeleting}
                    className="px-4 py-2 bg-gray-100 text-gray-800 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={selectionState.isDeleting}
                    className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {selectionState.isDeleting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Deleting...
                      </>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {selectionState.showArchiveConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100">
                <Archive className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                Archive Submissions
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to archive {selectionState.selectedIds.size} submission
                  {selectionState.selectedIds.size > 1 ? 's' : ''}? They will be moved to the archive where they can be restored later.
                </p>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 text-left">
                    Archive reason (optional)
                  </label>
                  <textarea
                    value={selectionState.archiveReason}
                    onChange={(e) => setSelectionState(prev => ({ ...prev, archiveReason: e.target.value }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    rows="3"
                    placeholder="Enter reason for archiving (optional)..."
                  />
                </div>
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={handleArchiveCancel}
                    disabled={selectionState.isArchiving}
                    className="px-4 py-2 bg-gray-100 text-gray-800 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkArchive}
                    disabled={selectionState.isArchiving}
                    className="px-4 py-2 bg-orange-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {selectionState.isArchiving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Archiving...
                      </>
                    ) : (
                      'Archive'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Confirmation Modal */}
      {selectionState.showAssignConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                <User className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                Assign Submissions
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 mb-4">
                  Choose a user to assign {selectionState.selectedIds.size} submission
                  {selectionState.selectedIds.size > 1 ? 's' : ''} to, or select "Unassigned" to remove current assignments.
                </p>
                <div className="text-left">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign to:
                  </label>
                  <select
                    value={selectionState.assignToUserId || ''}
                    onChange={(e) => {
                      const userId = e.target.value || null;
                      const user = assignmentState.users.find(u => u.id === userId);
                      setSelectionState(prev => ({
                        ...prev,
                        assignToUserId: userId,
                        assignToUserName: user ? user.displayName : 'Unassigned'
                      }));
                    }}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Unassigned</option>
                    {assignmentState.users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.displayName}
                      </option>
                    ))}
                  </select>
                  {assignmentState.loadingUsers && (
                    <p className="text-xs text-gray-500 mt-1">Loading users...</p>
                  )}
                </div>
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={handleAssignCancel}
                    disabled={selectionState.isAssigning}
                    className="px-4 py-2 bg-gray-100 text-gray-800 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkAssign}
                    disabled={selectionState.isAssigning}
                    className="px-4 py-2 bg-blue-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {selectionState.isAssigning ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Assigning...
                      </>
                    ) : (
                      selectionState.assignToUserId ? 'Assign' : 'Unassign'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionsPage;
